/**
 * AudienceBuilder — GA-style audience creator (FEAT-ANALYTICS-002). Pick
 * conditions from the dimension catalog, combine with AND/OR, watch the live
 * user estimate, name it, and save. Themed with xNotify brand tokens.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, Input, Select, HStack, VStack, Box, Flex, Text, IconButton, Menu, MenuButton,
  MenuList, MenuItem, MenuGroup, MenuOptionGroup, MenuItemOption, Badge, useToast, Spinner,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiUsers, FiChevronDown } from 'react-icons/fi';
import { CATEGORIES, getDim, OPERATOR_LABELS, defaultCondition } from './dimensions';
import { cleanDefinition, hasValidConditions } from './audienceParam';
import { estimateAudience, createAudience, updateAudience, listSegments } from './audienceApi';
import { useDimensionValues } from '../api';

// Tenant cohorts/segments, fetched once per modal open (cached for the session).
let _segmentsCache = null;
function useSegments() {
  const [segments, setSegments] = useState(_segmentsCache || []);
  useEffect(() => {
    if (_segmentsCache) return;
    let alive = true;
    listSegments().then((s) => { _segmentsCache = s; if (alive) setSegments(s); });
    return () => { alive = false; };
  }, []);
  return segments;
}

// Multi-select of existing Cohorts/Segments → stores an array of cohort ids.
function SegmentValue({ cond, onChange }) {
  const segments = useSegments();
  const arr = Array.isArray(cond.value) ? cond.value.map(String) : [];
  const byId = Object.fromEntries(segments.map((s) => [s.id, s]));
  const label = arr.length ? arr.map((id) => (byId[id] ? byId[id].name : id)).join(', ') : (segments.length ? 'select segment(s)' : 'no segments');
  return (
    <Menu closeOnSelect={false}>
      <MenuButton as={Button} size="sm" variant="outline" bg="white" minW="200px" maxW="320px" textAlign="left" fontWeight="400" isDisabled={!segments.length}>
        <Text noOfLines={1} fontSize="sm">{label}</Text>
      </MenuButton>
      <MenuList maxH="300px" overflowY="auto" minW="260px">
        <MenuOptionGroup type="checkbox" value={arr} onChange={(v) => onChange({ ...cond, value: Array.isArray(v) ? v : [v] })}>
          {segments.map((s) => (
            <MenuItemOption key={s.id} value={s.id} fontSize="sm">
              {s.name}{s.count != null ? ` (${Number(s.count).toLocaleString()})` : ''}
            </MenuItemOption>
          ))}
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  );
}

const TIMEFRAMES = [
  ['all_time', 'any time'], ['last_24_hours', 'last 24 hours'], ['last_7_days', 'last 7 days'],
  ['last_30_days', 'last 30 days'], ['last_90_days', 'last 90 days'], ['last_year', 'last 12 months'],
];
const COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2'];

// Keep the currently-selected value present as an option even if it's not in
// the fetched list (editing a saved audience, or a value absent from recent
// data) — a Chakra Select renders blank otherwise (ANTI-12).
function withCurrent(options, current) {
  if (!current || options.includes(current)) return options;
  return [current, ...options];
}

// A single string value — strict dropdown for is/is-not, free-text with a
// suggestions datalist for the substring operators, plain input as a fallback.
function SingleStringValue({ cond, dimKey, uid, onChange }) {
  const { values, loading } = useDimensionValues(dimKey);
  const set = (value) => onChange({ ...cond, value });
  const cur = Array.isArray(cond.value) ? '' : (cond.value ?? '');
  const op = cond.operator;
  if ((op === 'eq' || op === 'ne') && values.length > 0) {
    return (
      <Select size="sm" w="auto" minW="180px" maxW="260px" placeholder={loading ? 'loading…' : 'select value'} value={cur} onChange={(e) => set(e.target.value)}>
        {withCurrent(values, cur).map((v) => <option key={v} value={v}>{v}</option>)}
      </Select>
    );
  }
  const listId = `xn-dl-${uid}`;
  return (
    <>
      <Input size="sm" flex="1" maxW="240px" list={values.length ? listId : undefined} placeholder="value" value={cur} onChange={(e) => set(e.target.value)} />
      {values.length > 0 && <datalist id={listId}>{values.map((v) => <option key={v} value={v} />)}</datalist>}
    </>
  );
}

// Multiple string values (is one of / is not one of) — a checkbox dropdown that
// produces an array; falls back to a comma input when no values are known.
function MultiStringValue({ cond, dimKey, onChange }) {
  const { values } = useDimensionValues(dimKey);
  const arr = Array.isArray(cond.value)
    ? cond.value
    : String(cond.value || '').split(',').map((s) => s.trim()).filter(Boolean);
  const set = (vals) => onChange({ ...cond, value: vals });
  if (values.length === 0) {
    return <Input size="sm" flex="1" placeholder="value1, value2, …" value={arr.join(', ')} onChange={(e) => onChange({ ...cond, value: e.target.value })} maxW="240px" />;
  }
  const options = [...new Set([...arr, ...values])];
  return (
    <Menu closeOnSelect={false}>
      <MenuButton as={Button} size="sm" variant="outline" bg="white" rightIcon={<FiChevronDown />} minW="180px" textAlign="left" fontWeight="400">
        {arr.length ? `${arr.length} selected` : 'select values'}
      </MenuButton>
      <MenuList maxH="280px" overflowY="auto" minW="220px">
        <MenuOptionGroup type="checkbox" value={arr} onChange={(v) => set(Array.isArray(v) ? v : [v])}>
          {options.map((v) => <MenuItemOption key={v} value={v} fontSize="sm">{v}</MenuItemOption>)}
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  );
}

function EventValue({ cond, onChange }) {
  const { values, loading } = useDimensionValues('event');
  const set = (value) => onChange({ ...cond, value });
  return (
    <HStack flex="1" spacing={2} flexWrap="wrap">
      {values.length > 0 ? (
        <Select size="sm" w="auto" minW="190px" maxW="260px" placeholder={loading ? 'loading…' : 'select event'} value={cond.value} onChange={(e) => set(e.target.value)}>
          {withCurrent(values, cond.value).map((v) => <option key={v} value={v}>{v}</option>)}
        </Select>
      ) : (
        <Input size="sm" placeholder="event name" value={cond.value} onChange={(e) => set(e.target.value)} maxW="200px" />
      )}
      {cond.operator === 'count' && (
        <>
          <Select size="sm" w="auto" value={cond.count_op} onChange={(e) => onChange({ ...cond, count_op: e.target.value })}>
            <option value="gte">≥</option><option value="lte">≤</option><option value="gt">&gt;</option><option value="lt">&lt;</option>
          </Select>
          <Input size="sm" type="number" w="70px" value={cond.count} onChange={(e) => onChange({ ...cond, count: Number(e.target.value) })} />
        </>
      )}
      <Text fontSize="xs" color="gray.400">in</Text>
      <Select size="sm" w="auto" value={cond.timeframe} onChange={(e) => onChange({ ...cond, timeframe: e.target.value })}>
        {TIMEFRAMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </Select>
    </HStack>
  );
}

function ValueInput({ cond, dim, uid, onChange }) {
  const set = (value) => onChange({ ...cond, value });
  if (dim.kind === 'segment') return <SegmentValue cond={cond} onChange={onChange} />;
  if (dim.kind === 'event') return <EventValue cond={cond} onChange={onChange} />;
  if (dim.kind === 'time') {
    return <HStack flex="1"><Input size="sm" type="number" w="90px" value={cond.value} onChange={(e) => set(Number(e.target.value))} /><Text fontSize="xs" color="gray.500">days</Text></HStack>;
  }
  if (dim.kind === 'numeric') {
    if (cond.operator === 'between') {
      const v = Array.isArray(cond.value) ? cond.value : [0, 0];
      return (
        <HStack flex="1">
          <Input size="sm" type="number" w="100px" value={v[0]} onChange={(e) => set([Number(e.target.value), v[1]])} />
          <Text fontSize="xs" color="gray.400">and</Text>
          <Input size="sm" type="number" w="100px" value={v[1]} onChange={(e) => set([v[0], Number(e.target.value)])} />
        </HStack>
      );
    }
    return <Input size="sm" flex="1" type="number" value={cond.value} onChange={(e) => set(Number(e.target.value))} maxW="160px" />;
  }
  // string
  if (cond.operator === 'in' || cond.operator === 'not_in') return <MultiStringValue cond={cond} dimKey={dim.key} onChange={onChange} />;
  return <SingleStringValue cond={cond} dimKey={dim.key} uid={uid} onChange={onChange} />;
}

function ConditionRow({ cond, index, onChange, onRemove }) {
  const dim = getDim(cond.dim);
  if (!dim) return null;
  return (
    <Flex align="center" gap={2} bg="gray.50" borderRadius="8px" px={3} py={2} flexWrap="wrap">
      <Text fontSize="13px" fontWeight="600" color="gray.700" minW="110px">{dim.label}</Text>
      <Select size="sm" w="auto" value={cond.operator} onChange={(e) => onChange(index, { ...cond, operator: e.target.value })}>
        {dim.operators.map((op) => <option key={op} value={op}>{OPERATOR_LABELS[op] || op}</option>)}
      </Select>
      <ValueInput cond={cond} dim={dim} uid={index} onChange={(c) => onChange(index, c)} />
      <IconButton aria-label="remove" icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red" ml="auto" onClick={() => onRemove(index)} />
    </Flex>
  );
}

export function AudienceBuilder({ isOpen, onClose, onSaved, initial }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [color, setColor] = useState(COLORS[0]);
  const [matchMode, setMatchMode] = useState('and');
  const [conditions, setConditions] = useState([]);
  const [size, setSize] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (initial) {
      setName(initial.name || '');
      setColor(initial.color || COLORS[0]);
      const g = initial.definition?.groups?.[0];
      setMatchMode(g?.op || 'and');
      setConditions(g?.conditions || []);
    } else {
      setName(''); setColor(COLORS[0]); setMatchMode('and'); setConditions([]); setSize(null);
    }
  }, [isOpen, initial]);

  const draft = useMemo(() => ({ matchMode, conditions }), [matchMode, conditions]);
  const valid = hasValidConditions(draft);

  // Live estimate (debounced).
  useEffect(() => {
    if (!isOpen || !valid) { setSize(null); return; }
    const t = setTimeout(async () => {
      setEstimating(true);
      try { setSize(await estimateAudience(cleanDefinition(draft))); } catch (_) { setSize(null); } finally { setEstimating(false); }
    }, 450);
    return () => clearTimeout(t);
  }, [isOpen, valid, JSON.stringify(draft)]);

  const addCondition = (dimKey) => { const c = defaultCondition(dimKey); if (c) setConditions((p) => [...p, c]); };
  const changeCondition = (i, c) => setConditions((p) => p.map((x, j) => (j === i ? c : x)));
  const removeCondition = (i) => setConditions((p) => p.filter((_, j) => j !== i));

  const save = async () => {
    if (!name.trim()) { toast({ title: 'Name your audience', status: 'warning', duration: 2000 }); return; }
    if (!valid) { toast({ title: 'Add at least one complete condition', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), color, definition: cleanDefinition(draft) };
      if (initial?.id) await updateAudience(initial.id, payload);
      else await createAudience(payload);
      toast({ title: `Audience ${initial ? 'updated' : 'saved'}`, status: 'success', duration: 2000 });
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.message || e.message, status: 'error', duration: 4000 });
    } finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{initial ? 'Edit audience' : 'Build an audience'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <HStack>
              <Input placeholder="Audience name" value={name} onChange={(e) => setName(e.target.value)} />
              <HStack spacing={1}>
                {COLORS.map((c) => (
                  <Box key={c} w="22px" h="22px" borderRadius="full" bg={c} cursor="pointer" borderWidth="2px" borderColor={color === c ? 'gray.700' : 'transparent'} onClick={() => setColor(c)} />
                ))}
              </HStack>
            </HStack>

            <Flex align="center" gap={2}>
              <Text fontSize="sm" color="gray.600">Users who match</Text>
              <Select size="sm" w="auto" value={matchMode} onChange={(e) => setMatchMode(e.target.value)}>
                <option value="and">ALL of</option>
                <option value="or">ANY of</option>
              </Select>
              <Text fontSize="sm" color="gray.600">these conditions:</Text>
            </Flex>

            <VStack align="stretch" spacing={2}>
              {conditions.map((c, i) => <ConditionRow key={i} cond={c} index={i} onChange={changeCondition} onRemove={removeCondition} />)}
              {conditions.length === 0 && <Text fontSize="sm" color="gray.400" py={3} textAlign="center">No conditions yet — add one below.</Text>}
            </VStack>

            <Menu>
              <MenuButton as={Button} leftIcon={<FiPlus />} size="sm" variant="outline" alignSelf="flex-start">Add condition</MenuButton>
              <MenuList maxH="340px" overflowY="auto">
                {CATEGORIES.map((cat) => (
                  <MenuGroup key={cat.key} title={cat.label}>
                    {cat.dims.map((d) => <MenuItem key={cat.key + d.key} onClick={() => addCondition(d.key)} fontSize="sm" pl={6}>{d.label}</MenuItem>)}
                  </MenuGroup>
                ))}
              </MenuList>
            </Menu>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack mr="auto" color="gray.600" fontSize="sm">
            <FiUsers />
            <Text>Estimated users:</Text>
            {estimating ? <Spinner size="xs" /> : <Badge colorScheme="blue" fontSize="sm">{size != null ? size.toLocaleString() : '—'}</Badge>}
          </HStack>
          <Button variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button colorScheme="blue" onClick={save} isLoading={saving} isDisabled={!valid}>{initial ? 'Save changes' : 'Save audience'}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default AudienceBuilder;
